/** Analytics → Competition deep-dive dashboard.
 *
 *  One scrolling page driven by the shared ``<RangeChips>`` time
 *  filter. Each widget loads independently against its own endpoint
 *  under ``/api/analytics/competition/*`` so a slow query in one card
 *  can't block the others; per-widget error / loading state stays
 *  scoped. Auto-refreshes every 30s — same cadence as the Dashboard's
 *  competition poller, mostly to keep "in-flight" rows fresh.
 *
 *  Click any agency on any widget → drills to the existing
 *  ``/competition/{agencyId}`` detail page (no duplication of that
 *  surface here).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, api } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import {
  RangeChips,
  buildRangeQuery,
  todayLocalISO,
  addDaysLocal,
  type CustomRange,
  type Range,
} from "@/components/RangeChips";
import { Skeleton } from "@/components/Skeleton";
import { Sparkline } from "@/components/Sparkline";

/** Shared identity slug returned by every analytics endpoint. Keep in
 *  sync with ``app/schemas/analytics.py:AgencyRefMini``. */
interface AgencyRefMini {
  id: string;
  platform: "myhome" | "ss" | string;
  external_id: string;
  name: string;
  logo_url: string | null;
  color_hex: string | null;
}

interface WindowMeta {
  from: string;
  to: string;
  label?: string;
}

interface OverviewOut {
  window: WindowMeta;
  active_firms: number;
  active_firms_prev: number;
  active_brokers: number;
  active_brokers_prev: number;
  new_agencies: number;
  dormant_agencies: number;
  active_listings: number;
  listings_by_platform: { myhome: number; ss: number };
}

interface GrowthRow {
  agency: AgencyRefMini;
  current: number;
  prev: number;
  delta_abs: number;
  delta_pct: number | null;
  daily: number[];
}

interface GrowthOut {
  window: WindowMeta;
  kind: "agency" | "agent";
  growers: GrowthRow[];
  shrinkers: GrowthRow[];
}

interface NeighborhoodAgencyRow {
  agency: AgencyRefMini;
  listings: number;
  share_pct: number;
  prev_rank: number | null;
}

interface NeighborhoodBlock {
  neighborhood: string;
  total_listings: number;
  top_agencies: NeighborhoodAgencyRow[];
  other_listings: number;
}

interface NeighborhoodsOut {
  window: WindowMeta;
  blocks: NeighborhoodBlock[];
}

interface ChurnPoint {
  date: string;
  entered: number;
  dormant: number;
  total_active: number;
}

interface ChurnOut {
  window: WindowMeta;
  bucket: "day" | "week";
  points: ChurnPoint[];
}

interface FirmRow {
  agency: AgencyRefMini;
  agents_count_known: number | null;
  brokers_observed: number;
  active_listings: number;
  avg_price_usd: number | null;
  top_neighborhoods: string[];
  daily_listings: number[];
  current_window: number;
  prev_window: number;
  delta_pct: number | null;
}

interface FirmsOut {
  window: WindowMeta;
  total: number;
  limit: number;
  offset: number;
  items: FirmRow[];
}

interface PricingRow {
  agency: AgencyRefMini;
  total: number;
  q1: number;
  q2: number;
  q3: number;
  q4: number;
}

interface PricingOut {
  window: WindowMeta;
  items: PricingRow[];
}

interface PlatformSide {
  platform: string;
  active_firms: number;
  active_brokers: number;
  listings_today: number;
  listings_window: number;
}

interface PlatformSplitOut {
  window: WindowMeta;
  myhome: PlatformSide;
  ss: PlatformSide;
}

interface WeeklyVolumeRow {
  week_start: string; // ISO date, Mon
  week_end: string; // ISO date, Sun
  days_elapsed: number;
  is_current: boolean;
  firms_total: number;
  firms_avg_per_day: number;
  brokers_total: number;
  brokers_avg_per_day: number;
  overall_total: number;
  overall_avg_per_day: number;
}

interface WeeklyVolumeHistoryOut {
  weeks: WeeklyVolumeRow[];
}

interface WeeklyCadenceRow {
  agency: AgencyRefMini;
  daily: number[]; // 7 ints, Mon..Sun
  total: number;
  avg_per_day: number;
}

interface WeeklyCadenceOut {
  week_start: string;
  week_end: string;
  days_elapsed: number;
  is_current: boolean;
  firms: WeeklyCadenceRow[];
  brokers: WeeklyCadenceRow[];
}

export function AnalyticsCompetition() {
  const [range, setRange] = useState<Range>("today");
  const [customRange, setCustomRange] = useState<CustomRange>(() => {
    const today = todayLocalISO();
    return { from: addDaysLocal(today, -29), to: today };
  });
  const customKey =
    range === "custom" ? `${customRange.from}|${customRange.to}` : "";

  return (
    <div className="w-full space-y-4">
      <PageHeader
        title="Competition analytics"
        subtitle="Cross-market view of who's posting, where, and how their volume is shifting."
        actions={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <AgencyLookup />
            <RangeChips
              value={range}
              onChange={setRange}
              customRange={customRange}
              onCustomChange={setCustomRange}
            />
          </div>
        }
      />

      <HeroStrip range={range} customRange={customRange} customKey={customKey} />
      <PlatformSplitStrip range={range} customRange={customRange} customKey={customKey} />
      <WeeklyVolumeBlock />
      <WeeklyCadenceBlock />
      <GrowthBlock range={range} customRange={customRange} customKey={customKey} />
      <NeighborhoodsBlock range={range} customRange={customRange} customKey={customKey} />
      <ChurnBlock range={range} customRange={customRange} customKey={customKey} />
      <FirmsBlock range={range} customRange={customRange} customKey={customKey} />
      <PricingBlock range={range} customRange={customRange} customKey={customKey} />
    </div>
  );
}

/** Hook factory — wraps the auto-refresh + cancellation boilerplate
 *  every widget needs. Returns ``[data, err]`` plus refreshes every
 *  30s while the page is mounted. */
function useAnalyticsEndpoint<T>(
  path: string,
  range: Range,
  customRange: CustomRange,
  customKey: string,
  extraQuery?: Record<string, string>,
): [T | null, string | null] {
  const [data, setData] = useState<T | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const extraKey = extraQuery ? JSON.stringify(extraQuery) : "";

  const load = useCallback(async (signal: AbortSignal) => {
    try {
      const qs = buildRangeQuery(range, customRange, extraQuery);
      const r = await api<T>(`${path}?${qs.toString()}`, { signal });
      if (signal.aborted) return;
      setData(r);
      setErr(null);
    } catch (e) {
      if (signal.aborted) return;
      if (e instanceof ApiError) setErr(e.message);
      else if (e instanceof Error) setErr(e.message);
      else setErr("Request failed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, range, customKey, extraKey]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    const t = window.setInterval(() => {
      load(ctrl.signal);
    }, 30_000);
    return () => {
      ctrl.abort();
      window.clearInterval(t);
    };
  }, [load]);

  return [data, err];
}

// ─── Hero strip ───────────────────────────────────────────────────────

function HeroStrip(props: {
  range: Range;
  customRange: CustomRange;
  customKey: string;
}) {
  const [data, err] = useAnalyticsEndpoint<OverviewOut>(
    "/api/analytics/competition/overview",
    props.range,
    props.customRange,
    props.customKey,
  );
  const firmsDelta =
    data == null
      ? null
      : data.active_firms - data.active_firms_prev;
  const brokersDelta =
    data == null
      ? null
      : data.active_brokers - data.active_brokers_prev;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <StatTile
        label="Active firms"
        value={data?.active_firms}
        deltaAbs={firmsDelta}
        tone="accent"
      />
      <StatTile
        label="Active brokers (solo)"
        value={data?.active_brokers}
        deltaAbs={brokersDelta}
      />
      <StatTile
        label="New this window"
        value={data?.new_agencies}
        hint="first-time observed"
        tone={data && data.new_agencies > 0 ? "emerald" : "neutral"}
      />
      <StatTile
        label="Went dormant"
        value={data?.dormant_agencies}
        hint="last seen >14d ago"
        tone={data && data.dormant_agencies > 0 ? "rose" : "neutral"}
      />
      {err && (
        <div className="col-span-2 sm:col-span-4 card border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-xs p-3">
          {err}
        </div>
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  hint,
  tone = "neutral",
  deltaAbs,
}: {
  label: string;
  value: number | string | undefined;
  hint?: string;
  tone?: "neutral" | "accent" | "emerald" | "amber" | "rose";
  deltaAbs?: number | null;
}) {
  const TONE: Record<NonNullable<typeof tone>, string> = {
    neutral: "text-slate-100",
    accent: "text-accent-300",
    emerald: "text-emerald-300",
    amber: "text-amber-300",
    rose: "text-rose-300",
  };
  return (
    <div className="card p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div
        className={`text-[22px] font-semibold tabular-nums leading-tight mt-1 ${TONE[tone]}`}
      >
        {value === undefined ? <Skeleton className="h-6 w-16" /> : value}
      </div>
      {deltaAbs != null && (
        <div
          className={`text-[10.5px] tabular-nums mt-0.5 ${
            deltaAbs > 0
              ? "text-emerald-400"
              : deltaAbs < 0
                ? "text-rose-400"
                : "text-slate-500"
          }`}
        >
          {deltaAbs > 0 ? "▲" : deltaAbs < 0 ? "▼" : "·"}{" "}
          {Math.abs(deltaAbs)} vs prior window
        </div>
      )}
      {hint && !deltaAbs && (
        <div className="text-[10.5px] text-slate-500 mt-0.5">{hint}</div>
      )}
    </div>
  );
}

// ─── Platform split ───────────────────────────────────────────────────

function PlatformSplitStrip(props: {
  range: Range;
  customRange: CustomRange;
  customKey: string;
}) {
  const [data, err] = useAnalyticsEndpoint<PlatformSplitOut>(
    "/api/analytics/competition/platform-split",
    props.range,
    props.customRange,
    props.customKey,
  );
  return (
    <div className="card p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">
        Platform comparison
      </div>
      {err && (
        <div className="text-rose-300 text-xs">{err}</div>
      )}
      {!data ? (
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <PlatformCard side={data.myhome} name="MyHome" />
          <PlatformCard side={data.ss} name="SS.ge" />
        </div>
      )}
    </div>
  );
}

function PlatformCard({ side, name }: { side: PlatformSide; name: string }) {
  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-lg p-3">
      <div className="text-[11.5px] font-semibold text-slate-200 mb-2">
        {name}
      </div>
      <div className="grid grid-cols-2 gap-2 text-[12px]">
        <KV label="Active firms" value={side.active_firms} />
        <KV label="Solo brokers" value={side.active_brokers} />
        <KV label="Today" value={side.listings_today} />
        <KV label="Window total" value={side.listings_window} />
      </div>
    </div>
  );
}

function KV({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="tabular-nums text-slate-100">{value}</div>
    </div>
  );
}

// ─── Growth ───────────────────────────────────────────────────────────

function GrowthBlock(props: {
  range: Range;
  customRange: CustomRange;
  customKey: string;
}) {
  const [kind, setKind] = useState<"agency" | "agent">("agency");
  const [data, err] = useAnalyticsEndpoint<GrowthOut>(
    "/api/analytics/competition/growth",
    props.range,
    props.customRange,
    props.customKey,
    { kind, limit: "10" },
  );
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          Growers + shrinkers (vs prior window)
        </div>
        <div className="inline-flex bg-ink-900/60 border border-white/10 rounded-lg p-0.5">
          {(["agency", "agent"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={`px-2.5 py-1 rounded text-[11.5px] transition ${
                kind === k
                  ? "bg-accent-500/20 text-accent-200"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {k === "agency" ? "Firms" : "Brokers"}
            </button>
          ))}
        </div>
      </div>
      {err && <div className="text-rose-300 text-xs mb-2">{err}</div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <GrowthList
          title="Fastest growing"
          rows={data?.growers ?? null}
          tone="emerald"
        />
        <GrowthList
          title="Fastest shrinking"
          rows={data?.shrinkers ?? null}
          tone="rose"
        />
      </div>
    </div>
  );
}

function GrowthList({
  title,
  rows,
  tone,
}: {
  title: string;
  rows: GrowthRow[] | null;
  tone: "emerald" | "rose";
}) {
  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-lg p-3">
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
        {title}
      </div>
      {rows == null ? (
        <div className="space-y-1.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-7" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-[12px] text-slate-500">No movement in window.</div>
      ) : (
        <ul className="space-y-1">
          {rows.map((r) => (
            <li key={r.agency.id}>
              <Link
                to={`/competition/${r.agency.id}`}
                className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-white/[0.04] text-[12.5px]"
              >
                {r.agency.logo_url ? (
                  <img
                    src={r.agency.logo_url}
                    alt=""
                    className="w-5 h-5 rounded shrink-0 object-cover"
                    loading="lazy"
                  />
                ) : (
                  <span
                    className="w-5 h-5 rounded shrink-0"
                    style={{
                      background: r.agency.color_hex ?? "#27272a",
                    }}
                  />
                )}
                <span className="text-slate-200 truncate flex-1">
                  {r.agency.name}
                </span>
                <span className="tabular-nums text-slate-400 shrink-0">
                  {r.current}
                </span>
                <span
                  className={`text-[10.5px] tabular-nums shrink-0 ${
                    tone === "emerald" ? "text-emerald-300" : "text-rose-300"
                  }`}
                >
                  {r.delta_pct == null
                    ? "new"
                    : `${r.delta_pct > 0 ? "+" : ""}${r.delta_pct}%`}
                </span>
                {r.daily.length > 0 && (
                  <span className="w-16 shrink-0">
                    <Sparkline data={r.daily} height={18} />
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Neighborhoods ────────────────────────────────────────────────────

function NeighborhoodsBlock(props: {
  range: Range;
  customRange: CustomRange;
  customKey: string;
}) {
  const [data, err] = useAnalyticsEndpoint<NeighborhoodsOut>(
    "/api/analytics/competition/neighborhoods",
    props.range,
    props.customRange,
    props.customKey,
  );
  return (
    <div className="card p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">
        Market share by neighborhood
      </div>
      {err && <div className="text-rose-300 text-xs mb-2">{err}</div>}
      {!data ? (
        <Skeleton className="h-48" />
      ) : data.blocks.length === 0 ? (
        <div className="text-[12.5px] text-slate-500">
          No neighborhood data in window.
        </div>
      ) : (
        <div className="space-y-3">
          {data.blocks.map((b) => (
            <NeighborhoodBar key={b.neighborhood} block={b} />
          ))}
        </div>
      )}
    </div>
  );
}

function NeighborhoodBar({ block }: { block: NeighborhoodBlock }) {
  const total = block.total_listings || 1;
  // Default palette for agencies without color_hex — cycle so adjacent
  // segments contrast. ``Others`` always slate.
  const FALLBACK = [
    "#3b82f6",
    "#f97316",
    "#10b981",
    "#a855f7",
    "#facc15",
    "#ec4899",
  ];
  return (
    <div>
      <div className="flex items-baseline justify-between text-[11.5px] mb-1">
        <span className="text-slate-200 font-medium">{block.neighborhood}</span>
        <span className="text-slate-500 tabular-nums">
          {block.total_listings} listings
        </span>
      </div>
      <div className="flex w-full h-5 rounded overflow-hidden bg-ink-900/60">
        {block.top_agencies.map((row, i) => {
          const pct = (row.listings / total) * 100;
          const bg = row.agency.color_hex || FALLBACK[i % FALLBACK.length];
          const currRank = i + 1;
          const shift =
            row.prev_rank != null ? row.prev_rank - currRank : null;
          const arrow =
            shift == null
              ? ""
              : shift > 0
                ? `↑${shift}`
                : shift < 0
                  ? `↓${-shift}`
                  : "";
          return (
            <Link
              key={row.agency.id}
              to={`/competition/${row.agency.id}`}
              style={{ width: `${pct}%`, background: bg }}
              className="h-full hover:opacity-90 transition-opacity flex items-center justify-center"
              title={`${row.agency.name} — ${row.share_pct}% (${row.listings})${
                shift != null ? ` · ${arrow} from #${row.prev_rank}` : ""
              }`}
            >
              {pct > 8 && (
                <span className="text-[10px] font-medium text-white/90 truncate px-1">
                  {row.agency.name.split(/\s+/)[0]}
                </span>
              )}
            </Link>
          );
        })}
        {block.other_listings > 0 && (
          <div
            style={{ width: `${(block.other_listings / total) * 100}%` }}
            className="h-full bg-slate-700/50"
            title={`Others — ${block.other_listings} listings`}
          />
        )}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[10.5px] text-slate-500">
        {block.top_agencies.map((row, i) => {
          const currRank = i + 1;
          const shift =
            row.prev_rank != null ? row.prev_rank - currRank : null;
          return (
            <span key={row.agency.id} className="inline-flex items-center gap-1">
              <span
                className="w-2 h-2 rounded-sm"
                style={{
                  background: row.agency.color_hex || FALLBACK[i % FALLBACK.length],
                }}
              />
              <span className="text-slate-300">{row.agency.name}</span>
              <span className="text-slate-500 tabular-nums">
                {row.share_pct}%
              </span>
              {shift != null && shift !== 0 && (
                <span
                  className={
                    shift > 0 ? "text-emerald-400" : "text-rose-400"
                  }
                >
                  {shift > 0 ? `↑${shift}` : `↓${-shift}`}
                </span>
              )}
              {row.prev_rank == null && (
                <span className="text-accent-400">new</span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ─── Churn ────────────────────────────────────────────────────────────

function ChurnBlock(props: {
  range: Range;
  customRange: CustomRange;
  customKey: string;
}) {
  const [bucket, setBucket] = useState<"day" | "week">(
    props.range === "30d" || props.range === "custom" ? "week" : "day",
  );
  const [data, err] = useAnalyticsEndpoint<ChurnOut>(
    "/api/analytics/competition/churn",
    props.range,
    props.customRange,
    props.customKey,
    { bucket },
  );
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          Entry / exit churn
        </div>
        <div className="inline-flex bg-ink-900/60 border border-white/10 rounded-lg p-0.5">
          {(["day", "week"] as const).map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => setBucket(b)}
              className={`px-2.5 py-1 rounded text-[11.5px] transition ${
                bucket === b
                  ? "bg-accent-500/20 text-accent-200"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {b === "day" ? "Daily" : "Weekly"}
            </button>
          ))}
        </div>
      </div>
      {err && <div className="text-rose-300 text-xs mb-2">{err}</div>}
      {!data ? (
        <Skeleton className="h-32" />
      ) : data.points.length === 0 ? (
        <div className="text-[12.5px] text-slate-500">
          No churn data in window.
        </div>
      ) : (
        <ChurnChart points={data.points} />
      )}
    </div>
  );
}

function ChurnChart({ points }: { points: ChurnPoint[] }) {
  // Compact in-page SVG chart. Two stacked bars per bucket (entered green,
  // dormant rose), plus a line for total_active overlaid.
  const W = 800;
  const H = 160;
  const pad = { l: 30, r: 28, t: 8, b: 18 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const n = points.length;
  const colW = innerW / Math.max(1, n);
  const maxBar = Math.max(
    1,
    ...points.map((p) => Math.max(p.entered, p.dormant)),
  );
  const maxLine = Math.max(1, ...points.map((p) => p.total_active));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32">
      {/* Bars */}
      {points.map((p, i) => {
        const x = pad.l + i * colW + colW * 0.15;
        const w = colW * 0.35;
        const eh = (p.entered / maxBar) * (innerH * 0.7);
        const dh = (p.dormant / maxBar) * (innerH * 0.7);
        return (
          <g key={i}>
            <rect
              x={x}
              y={pad.t + (innerH - eh)}
              width={w}
              height={eh}
              fill="rgb(16 185 129 / 0.7)"
            >
              <title>
                {p.date}: {p.entered} entered
              </title>
            </rect>
            <rect
              x={x + w + colW * 0.05}
              y={pad.t + (innerH - dh)}
              width={w}
              height={dh}
              fill="rgb(244 63 94 / 0.7)"
            >
              <title>
                {p.date}: {p.dormant} dormant
              </title>
            </rect>
          </g>
        );
      })}
      {/* Total-active line */}
      <polyline
        fill="none"
        stroke="rgb(99 102 241 / 0.85)"
        strokeWidth="1.5"
        points={points
          .map((p, i) => {
            const x = pad.l + i * colW + colW * 0.5;
            const y = pad.t + innerH - (p.total_active / maxLine) * innerH;
            return `${x},${y}`;
          })
          .join(" ")}
      />
      {/* Legend */}
      <g transform={`translate(${pad.l}, ${H - 4})`} fontSize="9" fill="rgb(148 163 184)">
        <circle cx="4" cy="-4" r="3" fill="rgb(16 185 129 / 0.7)" />
        <text x="11" y="-1">entered</text>
        <circle cx="58" cy="-4" r="3" fill="rgb(244 63 94 / 0.7)" />
        <text x="65" y="-1">dormant</text>
        <line x1="118" y1="-4" x2="130" y2="-4" stroke="rgb(99 102 241)" strokeWidth="1.5" />
        <text x="134" y="-1">total active</text>
      </g>
    </svg>
  );
}

// ─── Firms table ──────────────────────────────────────────────────────

function FirmsBlock(props: {
  range: Range;
  customRange: CustomRange;
  customKey: string;
}) {
  const [sort, setSort] = useState<"active" | "agents" | "growth" | "name">(
    "active",
  );
  const [data, err] = useAnalyticsEndpoint<FirmsOut>(
    "/api/analytics/competition/firms",
    props.range,
    props.customRange,
    props.customKey,
    { sort, limit: "25" },
  );
  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between border-b border-white/5">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          Firms · broker-to-firm rollup
        </div>
        <div className="inline-flex bg-ink-900/60 border border-white/10 rounded-lg p-0.5">
          {(
            [
              ["active", "Active"],
              ["agents", "Agents"],
              ["growth", "Growth"],
              ["name", "Name"],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setSort(k)}
              className={`px-2.5 py-1 rounded text-[11.5px] transition ${
                sort === k
                  ? "bg-accent-500/20 text-accent-200"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {err && (
        <div className="px-4 py-2 text-rose-300 text-xs border-b border-rose-500/25 bg-rose-500/[0.04]">
          {err}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Firm</th>
              <th className="text-right px-4 py-2">Agents</th>
              <th className="text-right px-4 py-2">Active</th>
              <th className="text-right px-4 py-2">Growth</th>
              <th className="text-right px-4 py-2">Avg $</th>
              <th className="text-left px-4 py-2">Top neighborhoods</th>
              <th className="text-right px-4 py-2 pr-5">Trend</th>
            </tr>
          </thead>
          <tbody>
            {!data ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td colSpan={7} className="px-4 py-2">
                    <Skeleton className="h-6" />
                  </td>
                </tr>
              ))
            ) : data.items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                  No firms in window.
                </td>
              </tr>
            ) : (
              data.items.map((r) => (
                <tr
                  key={r.agency.id}
                  className="border-t border-white/5 hover:bg-white/[0.02]"
                >
                  <td className="px-4 py-1.5">
                    <Link
                      to={`/competition/${r.agency.id}`}
                      className="inline-flex items-center gap-2"
                    >
                      {r.agency.logo_url ? (
                        <img
                          src={r.agency.logo_url}
                          alt=""
                          className="w-5 h-5 rounded shrink-0 object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <span
                          className="w-5 h-5 rounded shrink-0"
                          style={{
                            background: r.agency.color_hex ?? "#27272a",
                          }}
                        />
                      )}
                      <span className="text-slate-200 truncate max-w-[220px]">
                        {r.agency.name}
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-1.5 text-right tabular-nums text-slate-300">
                    {r.agents_count_known ?? `~${r.brokers_observed}`}
                  </td>
                  <td className="px-4 py-1.5 text-right tabular-nums text-slate-200">
                    {r.active_listings}
                  </td>
                  <td
                    className={`px-4 py-1.5 text-right tabular-nums ${
                      r.delta_pct == null
                        ? "text-accent-300"
                        : r.delta_pct > 0
                          ? "text-emerald-300"
                          : r.delta_pct < 0
                            ? "text-rose-300"
                            : "text-slate-500"
                    }`}
                  >
                    {r.delta_pct == null
                      ? "new"
                      : `${r.delta_pct > 0 ? "+" : ""}${r.delta_pct}%`}
                  </td>
                  <td className="px-4 py-1.5 text-right tabular-nums text-slate-400">
                    {r.avg_price_usd
                      ? `$${Math.round(Number(r.avg_price_usd)).toLocaleString()}`
                      : "—"}
                  </td>
                  <td className="px-4 py-1.5 text-slate-400 truncate max-w-[200px]">
                    {r.top_neighborhoods.join(" · ") || "—"}
                  </td>
                  <td className="px-4 py-1.5 pr-5 text-right">
                    {r.daily_listings.length > 0 ? (
                      <span className="inline-block w-20">
                        <Sparkline data={r.daily_listings} height={18} />
                      </span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {data && (
        <div className="px-4 py-2 text-[11px] text-slate-500 border-t border-white/5">
          {Math.min(data.items.length, data.total)} of {data.total} firms shown
        </div>
      )}
    </div>
  );
}

// ─── Pricing heatmap ──────────────────────────────────────────────────

function PricingBlock(props: {
  range: Range;
  customRange: CustomRange;
  customKey: string;
}) {
  const [data, err] = useAnalyticsEndpoint<PricingOut>(
    "/api/analytics/competition/pricing",
    props.range,
    props.customRange,
    props.customKey,
  );
  const maxCell = useMemo(() => {
    if (!data) return 1;
    return Math.max(
      1,
      ...data.items.flatMap((r) => [r.q1, r.q2, r.q3, r.q4]),
    );
  }, [data]);
  return (
    <div className="card p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
        Pricing position
      </div>
      <div className="text-[11px] text-slate-500 mb-3">
        Top firms × market-wide price quartiles (Q1 budget → Q4 premium). Darker = more listings.
      </div>
      {err && <div className="text-rose-300 text-xs mb-2">{err}</div>}
      {!data ? (
        <Skeleton className="h-48" />
      ) : data.items.length === 0 ? (
        <div className="text-[12.5px] text-slate-500">
          No priced listings in window.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-1.5">Firm</th>
                <th className="text-right px-2 py-1.5">Q1</th>
                <th className="text-right px-2 py-1.5">Q2</th>
                <th className="text-right px-2 py-1.5">Q3</th>
                <th className="text-right px-2 py-1.5">Q4</th>
                <th className="text-right px-3 py-1.5">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.agency.id} className="border-t border-white/5">
                  <td className="px-3 py-1">
                    <Link
                      to={`/competition/${r.agency.id}`}
                      className="text-slate-200 hover:text-accent-300 truncate inline-block max-w-[220px]"
                    >
                      {r.agency.name}
                    </Link>
                  </td>
                  <HeatCell value={r.q1} max={maxCell} />
                  <HeatCell value={r.q2} max={maxCell} />
                  <HeatCell value={r.q3} max={maxCell} />
                  <HeatCell value={r.q4} max={maxCell} />
                  <td className="px-3 py-1 text-right tabular-nums text-slate-400">
                    {r.total}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Agency lookup (search box) ───────────────────────────────────────

interface SearchHit {
  id: string;
  platform: "myhome" | "ss" | string;
  external_id: string;
  name: string;
  user_type: string | null;
  logo_url: string | null;
  parent_agency_id: string | null;
  parent_name: string | null;
  active_listings: number;
}

interface SearchResponse {
  query: string;
  items: SearchHit[];
}

/** Header search box — resolves a UUID / external ID / MyHome-or-SS URL
 *  / name fragment to a MarketAgency row and navigates to its detail
 *  page. Debounced 250ms; popover dismisses on outside click or
 *  selection. ``Enter`` on a single hit jumps straight to it.
 *
 *  Built for the operator question "I have a userId from SS — does this
 *  broker exist in our DB, and if so what's their profile?" — pasting
 *  the URL or just the bare ID answers in one keypress. */
function AgencyLookup() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on outside-click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Debounced search.
  useEffect(() => {
    const trimmed = q.trim();
    if (!trimmed) {
      setHits(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const ctrl = new AbortController();
    const t = window.setTimeout(async () => {
      try {
        const r = await api<SearchResponse>(
          `/api/stats/competition/search?q=${encodeURIComponent(trimmed)}`,
          { signal: ctrl.signal },
        );
        if (ctrl.signal.aborted) return;
        setHits(r.items);
      } catch {
        if (!ctrl.signal.aborted) setHits([]);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => {
      ctrl.abort();
      window.clearTimeout(t);
    };
  }, [q]);

  const pick = (id: string) => {
    setOpen(false);
    setQ("");
    setHits(null);
    navigate(`/competition/${id}`);
  };

  return (
    <div ref={rootRef} className="relative flex-shrink-0">
      <div className="relative">
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => q && setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && hits && hits.length > 0) {
              pick(hits[0].id);
            }
          }}
          placeholder="Find broker / firm — UUID, ID, URL, name…"
          className="h-9 pl-7 pr-7 rounded-lg bg-ink-900/60 border border-white/10 text-[12px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-accent-500/60 focus:ring-2 focus:ring-accent-500/20 min-w-[260px]"
        />
        <svg
          className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        {q && (
          <button
            type="button"
            onClick={() => {
              setQ("");
              setHits(null);
              inputRef.current?.focus();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 text-sm leading-none"
            aria-label="Clear"
          >
            ×
          </button>
        )}
      </div>
      {open && q && (
        <div
          role="listbox"
          className="absolute right-0 top-[calc(100%+4px)] z-40 w-[380px] max-h-[420px] overflow-y-auto rounded-lg bg-ink-900 border border-white/10 shadow-card"
        >
          {loading && hits == null ? (
            <div className="p-3 text-[12px] text-slate-500">Searching…</div>
          ) : hits && hits.length === 0 ? (
            <div className="p-3 text-[12px] text-slate-500">
              No broker or firm matches{" "}
              <span className="text-slate-300 font-mono">{q.trim()}</span>.
              {/* Hint about how the live ingest catches up so the
                  operator knows a fresh listing might appear later. */}
              <div className="mt-1 text-[11px] text-slate-600">
                If this is a real account, it'll appear once one of their
                listings is re-scraped by the ingest tick.
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {hits?.map((h) => (
                <li key={h.id}>
                  <button
                    type="button"
                    onClick={() => pick(h.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.04]"
                  >
                    {h.logo_url ? (
                      <img
                        src={h.logo_url}
                        alt=""
                        className="w-6 h-6 rounded shrink-0 object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <span className="w-6 h-6 rounded shrink-0 bg-slate-700/50" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] text-slate-100 truncate">
                        {h.name}
                      </div>
                      <div className="text-[10.5px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                        <span className="uppercase tracking-wider">
                          {h.platform}
                        </span>
                        {h.user_type && (
                          <span className="text-slate-600">·</span>
                        )}
                        {h.user_type && <span>{h.user_type}</span>}
                        <span className="text-slate-600">·</span>
                        <span className="font-mono">{h.external_id.length > 16 ? `${h.external_id.slice(0, 8)}…` : h.external_id}</span>
                        {h.parent_name && (
                          <>
                            <span className="text-slate-600">·</span>
                            <span className="truncate">
                              under {h.parent_name}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className="text-[11px] tabular-nums text-slate-400 shrink-0">
                      {h.active_listings || "—"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="px-3 py-1.5 text-[10px] text-slate-600 border-t border-white/5">
            Search by UUID, MyHome/SS user ID, full listing URL, or name.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Weekly volume history (Mon-Sun aggregate avg/day) ────────────────

function WeeklyVolumeBlock() {
  const [data, setData] = useState<WeeklyVolumeHistoryOut | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const WEEKS = 8;

  useEffect(() => {
    const ctrl = new AbortController();
    let timer: number | undefined;
    const load = async () => {
      try {
        const r = await api<WeeklyVolumeHistoryOut>(
          `/api/analytics/competition/weekly-volume-history?weeks=${WEEKS}`,
          { signal: ctrl.signal },
        );
        if (!ctrl.signal.aborted) {
          setData(r);
          setErr(null);
        }
      } catch (e) {
        if (ctrl.signal.aborted) return;
        if (e instanceof ApiError) setErr(e.message);
        else if (e instanceof Error) setErr(e.message);
      }
    };
    load();
    timer = window.setInterval(load, 60_000);
    return () => {
      ctrl.abort();
      if (timer) window.clearInterval(timer);
    };
  }, []);

  // Display order: newest-first matches the response. The bar chart
  // below flips to chronological (oldest-left) so the trend reads
  // naturally — like every other timeseries on the page.
  const chartRows = useMemo(
    () => (data ? [...data.weeks].reverse() : []),
    [data],
  );

  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between gap-2 flex-wrap mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Weekly volume · Mon–Sun
          </div>
          <div className="text-[11.5px] text-slate-400 mt-0.5">
            Market-wide listings posted per week. Average = total ÷ days
            elapsed (so this week stays comparable to past weeks
            mid-stream).
          </div>
        </div>
      </div>
      {err && (
        <div className="text-rose-300 text-xs mb-2">{err}</div>
      )}

      {!data ? (
        <Skeleton className="h-32" />
      ) : (
        <>
          <WeeklyVolumeChart rows={chartRows} />
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-2 py-1.5">Week</th>
                  <th className="text-right px-2 py-1.5">Firms /d</th>
                  <th className="text-right px-2 py-1.5">Brokers /d</th>
                  <th className="text-right px-2 py-1.5">Overall /d</th>
                  <th className="text-right px-2 py-1.5">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.weeks.map((w) => (
                  <tr
                    key={w.week_start}
                    className={`border-t border-white/5 ${
                      w.is_current ? "bg-accent-500/[0.04]" : ""
                    }`}
                  >
                    <td className="px-2 py-1.5">
                      <span className="text-slate-200">
                        {formatWeekLabel(w)}
                      </span>
                      {w.is_current && (
                        <span className="ml-1.5 text-[9.5px] uppercase tracking-wider text-accent-300">
                          current · day {w.days_elapsed}/7
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">
                      {w.firms_avg_per_day.toFixed(1)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">
                      {w.brokers_avg_per_day.toFixed(1)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-100 font-semibold">
                      {w.overall_avg_per_day.toFixed(1)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">
                      {w.overall_total.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function formatWeekLabel(w: WeeklyVolumeRow): string {
  const fmt = (iso: string) => {
    const [y, m, dd] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1, dd);
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  return `${fmt(w.week_start)} – ${fmt(w.week_end)}`;
}

/** Stacked-bar trend chart — firms on bottom (accent), brokers on top
 *  (emerald). The current week's bar is rendered at half opacity so a
 *  partial reading isn't mistaken for a full week. Hover any segment
 *  for the exact /d value. */
function WeeklyVolumeChart({ rows }: { rows: WeeklyVolumeRow[] }) {
  const W = 800;
  const H = 180;
  const pad = { l: 36, r: 16, t: 12, b: 36 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const n = rows.length || 1;
  const colW = innerW / n;
  const maxStack = Math.max(
    1,
    ...rows.map((r) => r.firms_avg_per_day + r.brokers_avg_per_day),
  );
  // Round the max up to a friendly tick so the y-axis reads cleanly.
  const tick = niceTick(maxStack);
  const yMax = Math.ceil(maxStack / tick) * tick;
  const yToPx = (v: number) => pad.t + innerH - (v / yMax) * innerH;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44">
      {/* Y-axis grid + labels */}
      {[0, 0.5, 1].map((f) => {
        const v = Math.round(yMax * f);
        const y = yToPx(v);
        return (
          <g key={f}>
            <line
              x1={pad.l}
              x2={W - pad.r}
              y1={y}
              y2={y}
              stroke="rgb(148 163 184 / 0.12)"
              strokeWidth="1"
            />
            <text
              x={pad.l - 6}
              y={y + 3}
              textAnchor="end"
              fontSize="10"
              fill="rgb(148 163 184 / 0.7)"
            >
              {v.toLocaleString()}
            </text>
          </g>
        );
      })}
      {/* Bars */}
      {rows.map((r, i) => {
        const x = pad.l + i * colW + colW * 0.2;
        const w = colW * 0.6;
        const firmsTop = yToPx(r.firms_avg_per_day);
        const firmsH = innerH * (r.firms_avg_per_day / yMax);
        const brokersTop = yToPx(r.firms_avg_per_day + r.brokers_avg_per_day);
        const brokersH = innerH * (r.brokers_avg_per_day / yMax);
        const fade = r.is_current ? 0.55 : 1;
        return (
          <g key={r.week_start} style={{ opacity: fade }}>
            <rect
              x={x}
              y={firmsTop}
              width={w}
              height={firmsH}
              fill="rgb(99 102 241 / 0.85)"
            >
              <title>
                {formatWeekLabel(r)} · firms {r.firms_avg_per_day.toFixed(1)}/d
              </title>
            </rect>
            <rect
              x={x}
              y={brokersTop}
              width={w}
              height={brokersH}
              fill="rgb(16 185 129 / 0.85)"
            >
              <title>
                {formatWeekLabel(r)} · brokers {r.brokers_avg_per_day.toFixed(1)}/d
              </title>
            </rect>
            {/* Total label above the bar */}
            <text
              x={x + w / 2}
              y={brokersTop - 4}
              textAnchor="middle"
              fontSize="10"
              fill="rgb(226 232 240 / 0.9)"
            >
              {(r.overall_avg_per_day).toFixed(0)}
            </text>
            {/* Week label below */}
            <text
              x={x + w / 2}
              y={H - pad.b + 14}
              textAnchor="middle"
              fontSize="9.5"
              fill="rgb(148 163 184 / 0.75)"
            >
              {monthDay(r.week_start)}
            </text>
            {r.is_current && (
              <text
                x={x + w / 2}
                y={H - pad.b + 26}
                textAnchor="middle"
                fontSize="8.5"
                fill="rgb(196 181 253 / 0.85)"
              >
                day {r.days_elapsed}/7
              </text>
            )}
          </g>
        );
      })}
      {/* Legend */}
      <g transform={`translate(${pad.l + 4}, ${pad.t})`} fontSize="9" fill="rgb(148 163 184)">
        <rect width="8" height="8" fill="rgb(99 102 241 / 0.85)" />
        <text x="12" y="7">firms /d</text>
        <rect x="62" width="8" height="8" fill="rgb(16 185 129 / 0.85)" />
        <text x="74" y="7">brokers /d</text>
      </g>
    </svg>
  );
}

function monthDay(iso: string): string {
  const [, m, d] = iso.split("-").map(Number);
  const dt = new Date(2000, m - 1, d);
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Pick a friendly y-axis step for the given max value — 10/25/50/100/…
 *  so the gridlines read as round numbers. */
function niceTick(maxVal: number): number {
  if (maxVal <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(maxVal)));
  const mantissa = maxVal / pow;
  if (mantissa <= 1) return pow / 2;
  if (mantissa <= 2) return pow / 2;
  if (mantissa <= 5) return pow;
  return pow * 2;
}

// ─── Per-poster weekly cadence (firms + lone brokers leaderboard) ────

function WeeklyCadenceBlock() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [data, setData] = useState<WeeklyCadenceOut | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    let timer: number | undefined;
    const load = async () => {
      try {
        const r = await api<WeeklyCadenceOut>(
          `/api/analytics/competition/weekly-cadence?week_offset=${weekOffset}&limit=25`,
          { signal: ctrl.signal },
        );
        if (!ctrl.signal.aborted) {
          setData(r);
          setErr(null);
        }
      } catch (e) {
        if (ctrl.signal.aborted) return;
        if (e instanceof ApiError) setErr(e.message);
        else if (e instanceof Error) setErr(e.message);
      }
    };
    load();
    timer = window.setInterval(load, 60_000);
    return () => {
      ctrl.abort();
      if (timer) window.clearInterval(timer);
    };
  }, [weekOffset]);

  const headerLabel = data ? formatCadenceWeekRange(data) : "Loading…";

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between border-b border-white/5 flex-wrap gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Weekly cadence by firm + solo broker · Mon–Sun
          </div>
          <div className="text-[11.5px] text-slate-400 mt-0.5">
            {headerLabel}
          </div>
        </div>
        <div className="inline-flex bg-ink-900/60 border border-white/10 rounded-lg p-0.5">
          <button
            type="button"
            onClick={() => setWeekOffset((o) => Math.max(-12, o - 1))}
            className="px-2.5 py-1 text-[11.5px] text-slate-400 hover:text-slate-200 rounded disabled:opacity-40"
            disabled={weekOffset <= -12}
          >
            ← Prev
          </button>
          <button
            type="button"
            onClick={() => setWeekOffset(0)}
            className={`px-2.5 py-1 rounded text-[11.5px] transition ${
              weekOffset === 0
                ? "bg-accent-500/20 text-accent-200"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            This week
          </button>
          <button
            type="button"
            onClick={() => setWeekOffset((o) => Math.min(0, o + 1))}
            className="px-2.5 py-1 text-[11.5px] text-slate-400 hover:text-slate-200 rounded disabled:opacity-40"
            disabled={weekOffset >= 0}
          >
            Next →
          </button>
        </div>
      </div>
      {err && (
        <div className="px-4 py-2 text-rose-300 text-xs border-b border-rose-500/25 bg-rose-500/[0.04]">
          {err}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 p-3">
        <CadenceList
          title="Top broker companies"
          subtitle={
            data
              ? `${data.firms.length} firms · avg ÷ ${data.days_elapsed} day${data.days_elapsed === 1 ? "" : "s"}`
              : "Loading…"
          }
          rows={data?.firms ?? null}
          daysElapsed={data?.days_elapsed ?? 7}
        />
        <CadenceList
          title="Top solo agents"
          subtitle={
            data
              ? `${data.brokers.length} brokers · avg ÷ ${data.days_elapsed} day${data.days_elapsed === 1 ? "" : "s"}`
              : "Loading…"
          }
          rows={data?.brokers ?? null}
          daysElapsed={data?.days_elapsed ?? 7}
        />
      </div>
    </div>
  );
}

function formatCadenceWeekRange(d: WeeklyCadenceOut): string {
  const fmt = (iso: string) => {
    const [y, m, dd] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1, dd);
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  const tag = d.is_current
    ? ` · current · day ${d.days_elapsed}/7`
    : "";
  return `${fmt(d.week_start)} – ${fmt(d.week_end)}${tag}`;
}

function CadenceList({
  title,
  subtitle,
  rows,
  daysElapsed,
}: {
  title: string;
  subtitle: string;
  rows: WeeklyCadenceRow[] | null;
  daysElapsed: number;
}) {
  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-lg">
      <div className="px-3 py-2 border-b border-white/5">
        <div className="text-[11.5px] text-slate-200 font-semibold">
          {title}
        </div>
        <div className="text-[10.5px] text-slate-500">{subtitle}</div>
      </div>
      {rows == null ? (
        <div className="p-3 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-7" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="p-4 text-[12px] text-slate-500 text-center">
          No posting activity this week.
        </div>
      ) : (
        <ul className="divide-y divide-white/5 max-h-[420px] overflow-y-auto">
          {rows.map((r) => (
            <li key={r.agency.id}>
              <Link
                to={`/competition/${r.agency.id}`}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-white/[0.04] text-[12.5px]"
              >
                {r.agency.logo_url ? (
                  <img
                    src={r.agency.logo_url}
                    alt=""
                    className="w-5 h-5 rounded shrink-0 object-cover"
                    loading="lazy"
                  />
                ) : (
                  <span
                    className="w-5 h-5 rounded shrink-0"
                    style={{ background: r.agency.color_hex ?? "#27272a" }}
                  />
                )}
                <span className="text-slate-200 truncate flex-1 min-w-0">
                  {r.agency.name}
                </span>
                <DailyBars daily={r.daily} daysElapsed={daysElapsed} />
                <span
                  className="tabular-nums text-slate-100 shrink-0 text-right font-semibold"
                  style={{ minWidth: 44 }}
                  title={`${r.total} total this week`}
                >
                  {r.avg_per_day.toFixed(1)}
                </span>
                <span className="text-[10px] text-slate-500 shrink-0">/d</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Seven Mon..Sun bars rendered inline. Days that haven't elapsed
 *  yet on the current week tint to slate-700 so a partial-week reading
 *  is visually distinct from a complete one. */
function DailyBars({
  daily,
  daysElapsed,
}: {
  daily: number[];
  daysElapsed: number;
}) {
  const labels = ["M", "T", "W", "T", "F", "S", "S"];
  const max = Math.max(1, ...daily);
  return (
    <span
      className="inline-flex items-end gap-[2px] shrink-0"
      style={{ height: 22 }}
      aria-label={`Mon..Sun: ${daily.join(", ")}`}
    >
      {daily.map((v, i) => {
        const pct = (v / max) * 100;
        const future = i >= daysElapsed;
        return (
          <span
            key={i}
            className="flex flex-col items-center"
            style={{ width: 10 }}
          >
            <span
              title={`${labels[i]}: ${v}`}
              className={`block rounded-sm w-full ${
                future ? "bg-slate-700/30" : "bg-accent-500/70"
              }`}
              style={{
                height: `${Math.max(2, pct * 0.16)}px`,
                marginTop: "auto",
              }}
            />
          </span>
        );
      })}
    </span>
  );
}

function HeatCell({ value, max }: { value: number; max: number }) {
  const intensity = max <= 0 ? 0 : value / max;
  // Stops: 0 → transparent; small > faint blue; large > strong accent.
  const bg = `rgba(99, 102, 241, ${Math.min(0.85, intensity * 0.85)})`;
  return (
    <td className="px-2 py-1 text-right tabular-nums">
      <span
        className="inline-block min-w-[42px] px-1.5 py-0.5 rounded text-slate-100"
        style={{ background: bg }}
      >
        {value || ""}
      </span>
    </td>
  );
}
